import React from 'react'
import styled from '@emotion/styled'
import { WITHDRAW_ROOT_ROUTE } from '@mobi/account/Areas/Withdraw/Utils'
import { useBetAccount } from '@mobi/account/Hooks'
import { measurements } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { lessThan } from '@mobi/component-library/Utils/media'
import { hexColors } from '@mobi/settings'
import { deregisterBodyScrollLock, registerBodyScrollLock } from '@mobi/utils/functions'
import { useAccountDetailsQuery } from '@core/Areas/Account/hooks/useGetAccountDetails'
import { BalanceSection } from '@core/Areas/AppHeader/Components/HamburgerMenu/Components/BalanceSection/BalanceSection'
import { toggleIsDropDownOpen } from '@core/Areas/AppHeader/Store'
import { openDeposit } from '@core/Areas/Deposit'
import { postLogout } from '@core/Areas/Login/helpers/api'
import { ReverificationAlert } from '@core/Areas/Reverification/Alert'
import { SecondaryButtonStyled } from '@core/Components/Buttons/Buttons.styles'
import { PendingTicketsCountProvider } from '@core/Data/Account/PendingTicketsCount/PendingTicketsCountProvider'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { useAppDispatch, useAppSelector } from '@core/Store/hooks'
import { useObservableImmutable } from '@core/Utils/hooks'
import { appHistory } from '@core/Utils/routing/history'
import { getIsDropDownOpen } from '../../Store/selectors'
import { Badge } from '../Common/Badge'
import { OffersPanel } from './Components/OffersPanel'
import { refetch as refetchSpecialTokens } from './Components/helpers/useGetSpecialTokens'
import {
  AccountNameLabelStyled,
  AccountNumberLabelStyled,
  DepositWithdrawSectionStyled,
  AccountInfoWrapStyled as InfoWrap,
  ItemContainerStyled,
  LinkStyled,
  LogoutLinkStyled,
  SeparatorStyled,
} from './Wallet.styles'
import { useAnalytics } from './helpers/useAnalytics'

const LOADING_TEXT = 'Loading...'

export const Wallet: React.FC = () => {
  const dispatch = useAppDispatch()

  const accountDropDownRef = React.useRef<HTMLDivElement>(null)

  const [innerHeight, setInnerHeight] = React.useState(window.innerHeight)

  const isDropDownOpen = useAppSelector(getIsDropDownOpen)

  const { data: accountDetails } = useAccountDetailsQuery(isDropDownOpen)

  const { accountNumber = null, blackBookAlertCount = 0 } = accountDetails || {}

  const { data: betAccount } = useBetAccount({
    enabled: accountNumber !== null,
  })

  const { campaigns } = useObservableImmutable(userAccountState$, ['campaigns'])

  const campaignsCount = campaigns?.count() ?? 0

  useAnalytics()

  React.useEffect(() => {
    refetchSpecialTokens()
  }, [])

  React.useEffect(() => {
    let touchStartY: number | undefined = undefined

    const keyPressHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch(toggleIsDropDownOpen())
    }

    const touchStartHandler = (e: TouchEvent) => {
      if (accountDropDownRef.current) {
        const isAtBottom =
          accountDropDownRef.current.scrollTop + accountDropDownRef.current.offsetHeight >=
          accountDropDownRef.current.scrollHeight
        if (isAtBottom) {
          touchStartY = e?.touches?.[0].clientY
        } else {
          touchStartY = undefined
        }
      }
    }

    const touchEndHandler = (e: TouchEvent) => {
      if (touchStartY !== undefined) {
        const yDiff =
          touchStartY -
          (e.changedTouches[0]?.clientY === undefined ? touchStartY : e.changedTouches[0].clientY)

        if (yDiff > 100) {
          // It was a significant swipe
          dispatch(toggleIsDropDownOpen())
        }
      }
    }

    document.addEventListener('keydown', keyPressHandler)
    document.addEventListener('touchstart', touchStartHandler)
    document.addEventListener('touchend', touchEndHandler)
    return () => {
      document.removeEventListener('keydown', keyPressHandler)
      document.removeEventListener('touchstart', touchStartHandler)
      document.removeEventListener('touchend', touchEndHandler)
    }
  }, [isDropDownOpen, dispatch])

  React.useEffect(() => {
    const updateInnerHeight = () => {
      setTimeout(() => setInnerHeight(window.innerHeight), 200)
    }
    updateInnerHeight()
    window.addEventListener('resize', updateInnerHeight)
    return () => window.removeEventListener('resize', updateInnerHeight)
  }, [])

  React.useEffect(() => {
    const dropDownEl = accountDropDownRef.current
    if (!dropDownEl) return
    dropDownEl.scrollTop = 0
    registerBodyScrollLock(dropDownEl)
    return () => deregisterBodyScrollLock(dropDownEl)
  }, [])

  React.useEffect(() => {
    if (!accountDropDownRef.current) return
    accountDropDownRef.current.style.maxHeight = `calc(${innerHeight}px - ${measurements.mobi.headerHeight})`
  }, [innerHeight])

  return (
    <AccountDropDownWrapStyled
      ref={accountDropDownRef}
      aria-hidden={!isDropDownOpen}
      data-tid-account-dropdown
      data-wallet-closing={!isDropDownOpen}
    >
      <ReverificationAlert />

      <InfoWrap>
        <AccountNameLabelStyled data-tid-account-dropdown-account-name>
          {betAccount ? betAccount.PreferredName || betAccount.PrimaryAcctHolderName : LOADING_TEXT}
        </AccountNameLabelStyled>

        <AccountNumberLabelStyled data-tid-account-dropdown-account-number>
          Account # {accountNumber}
        </AccountNumberLabelStyled>
      </InfoWrap>

      {isDropDownOpen && <OffersPanel bonusBetCount={campaignsCount} isOpen={isDropDownOpen} />}

      <SeparatorStyled />

      <BalanceSection />

      <SeparatorStyled />

      <DepositWithdrawSectionStyled>
        <SecondaryButtonStyled
          role='button'
          data-tid-account-dropdown-deposit
          onClick={() => {
            dispatch(toggleIsDropDownOpen(false))
            openDeposit()
          }}
          aria-label='Deposit'
        >
          <Icon type='arrowdownwithtail' size='1.5rem' color={hexColors.studio} />
          Deposit
        </SecondaryButtonStyled>

        <SecondaryButtonStyled
          data-tid-account-dropdown-withdrawal
          onClick={() => {
            dispatch(toggleIsDropDownOpen(false))
            appHistory.push(WITHDRAW_ROOT_ROUTE)
          }}
        >
          <Icon type='arrowupwithtail' size='1.5rem' color={hexColors.studio} />
          Withdraw
        </SecondaryButtonStyled>
      </DepositWithdrawSectionStyled>

      <SeparatorStyled />

      <ItemContainerStyled>
        <LinkStyled to='/account/activity/pending' data-testid='pending-bets-link'>
          Pending bets
          <PendingTicketsCountProvider
            shouldForceRefetchPendingCount={isDropDownOpen}
            render={({ pendingBetsCount, doesCountExceedMax }) =>
              pendingBetsCount && pendingBetsCount > 0 ? (
                <Badge count={pendingBetsCount} showPlusIcon={doesCountExceedMax} />
              ) : null
            }
          />
        </LinkStyled>
      </ItemContainerStyled>

      <ItemContainerStyled>
        <LinkStyled to='/account/activity/results' data-tid-my-account-resulted>
          Resulted bets
        </LinkStyled>
      </ItemContainerStyled>

      <ItemContainerStyled>
        <LinkStyled
          to='/account/blackbook'
          data-tid-account-dropdown-blackbook
          data-testid='blackbook-link'
        >
          Blackbook
          {blackBookAlertCount > 0 && (
            <Badge count={blackBookAlertCount} data-testid='wallet-blackbook-badge' />
          )}
        </LinkStyled>
      </ItemContainerStyled>
      <SeparatorStyled />

      <ItemContainerStyled>
        <LinkStyled to='/account' data-tid-account-dropdown-account>
          My account
        </LinkStyled>
      </ItemContainerStyled>

      <ItemContainerStyled>
        <LogoutLinkStyled
          to=''
          onClick={e => {
            e.preventDefault()
            postLogout()
            dispatch(toggleIsDropDownOpen(false))
          }}
          data-tid-account-dropdown-logout
        >
          <Icon type='logout' size='1.7rem' color={hexColors.crimson} />
          Log out
        </LogoutLinkStyled>
      </ItemContainerStyled>
    </AccountDropDownWrapStyled>
  )
}

// ======
// Styles
// ======

const AccountDropDownWrapStyled = styled.div({
  position: 'absolute',
  top: measurements.mobi.headerHeight,
  boxSizing: 'border-box',
  background: hexColors.white,
  width: '100%',
  zIndex: -1,
  transform: 'translateY(-100%)',
  animation: 'cubic-bezier(0.16, 1, 0.3, 1) animateIn .2s forwards',
  padding: '1.3rem 1.7rem',
  overflow: 'auto',
  overscrollBehavior: 'contain',
  fontFamily: '"PF Square Sans Pro", Arial, sans-serif',

  ['&[data-wallet-closing="true"]']: {
    animation: 'cubic-bezier(0.16, 1, 0.3, 1) animateOut .2s forwards',
  },

  [lessThan('px350')]: {
    padding: '1rem 0.5rem',
  },

  ['@keyframes animateIn']: {
    from: { transform: 'translateY(-100%)' },
    to: { transform: 'translateY(0%)' },
  },

  ['@keyframes animateOut']: {
    from: { transform: 'translateY(0%)' },
    to: { transform: 'translateY(-100%)' },
  },
})
